<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'

import draggable from 'vuedraggable'

import BetaLinkBuildingCard from '@/components/beta-dashboard/molecules/BetaLinkBuildingCard.vue'
import BetaPageImageField from '@/components/beta-dashboard/molecules/BetaPageImageField.vue'
import BetaLinkBuildingDialog from '@/components/beta-dashboard/organisms/BetaLinkBuildingDialog.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { useApplicationStore } from '@/stores/application'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import { useSiteStore } from '@/stores/site'
import type {
    BetaLinkBuildingLink,
    BetaLinkBuildingLinkInput,
    BetaLinkBuildingPage,
} from '@/types/beta-dashboard'

type LinkBuildingImageField = 'background_file' | 'logo' | 'avatar'

const applicationStore = useApplicationStore()
const betaStore = useBetaDashboardStore()
const mediaManagerStore = useMediaManagerStore()
const siteStore = useSiteStore()

const pageDraft = reactive<BetaLinkBuildingPage>({
    logo: '',
    avatar: '',
    name: '',
    logo_type: '1',
    route: '',
    background_file: '',
    description: '',
})
const pageBaseline = ref('')
const dialogOpen = ref(false)
const linkForEdit = ref<BetaLinkBuildingLink | null>(null)
const savingPage = ref(false)
const savingLink = ref(false)
const deletingIds = ref<number[]>([])
const sorting = ref(false)

watch(
    () => betaStore.linkBuildingPage,
    (page) => {
        if (!page) return
        Object.assign(pageDraft, JSON.parse(JSON.stringify(page)))
        pageBaseline.value = JSON.stringify(pageDraft)
    },
    { deep: true, immediate: true },
)

onBeforeMount(() => betaStore.loadLinkBuilding())

const pageDirty = computed(() => JSON.stringify(pageDraft) !== pageBaseline.value)
const publicUrl = computed(() => {
    if (!siteStore.url) return ''
    const base = /^https?:\/\//.test(String(siteStore.url))
        ? String(siteStore.url)
        : `https://${siteStore.url}`

    return `${base.replace(/\/$/, '')}/links`
})

const chooseImage = (field: LinkBuildingImageField) => {
    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setAction((media: any) => {
        if (!media?.content) return
        pageDraft[field] = media.content
    })
    mediaManagerStore.setIsOpenModal(true)
}

const savePage = async () => {
    if (!pageDirty.value || savingPage.value) return

    savingPage.value = true
    const toastId: ToastId = notification.loading('Atualizando página de links…')
    try {
        await betaStore.saveLinkBuildingPage(JSON.parse(JSON.stringify(pageDraft)))
        pageBaseline.value = JSON.stringify(pageDraft)
        notification.update(toastId, 'Página de links atualizada com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível atualizar a página de links.',
            'error',
        )
    } finally {
        savingPage.value = false
    }
}

const openCreate = () => {
    linkForEdit.value = null
    dialogOpen.value = true
}

const openEdit = (link: BetaLinkBuildingLink) => {
    linkForEdit.value = link
    dialogOpen.value = true
}

const closeDialog = () => {
    if (savingLink.value) return
    dialogOpen.value = false
    linkForEdit.value = null
}

const saveLink = async (link: BetaLinkBuildingLinkInput) => {
    if (savingLink.value) return

    savingLink.value = true
    const editing = Boolean(linkForEdit.value?.id)
    const toastId: ToastId = notification.loading(editing ? 'Atualizando link…' : 'Adicionando link…')
    try {
        if (linkForEdit.value) {
            await betaStore.editLinkBuildingLink({
                ...linkForEdit.value,
                ...link,
                id: linkForEdit.value.id,
            })
        } else {
            await betaStore.addLinkBuildingLink(link)
        }

        notification.update(
            toastId,
            editing ? 'Link atualizado com sucesso.' : 'Link adicionado com sucesso.',
            'success',
        )
        dialogOpen.value = false
        linkForEdit.value = null
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível salvar o link.',
            'error',
        )
    } finally {
        savingLink.value = false
    }
}

const sortLinks = async () => {
    if (sorting.value) return

    sorting.value = true
    try {
        await betaStore.saveLinkBuildingOrder()
        notification.simple('Ordem dos links atualizada.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível ordenar os links.',
            'error',
        )
    } finally {
        sorting.value = false
    }
}

const removeLink = async (link: BetaLinkBuildingLink) => {
    const confirmed = await modal.confirmation(
        'Excluir link?',
        `“${link.route_name}” será removido da sua página de links.`,
        {
            type: 'danger',
            textConfirm: 'Sim, excluir',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed || deletingIds.value.includes(link.id)) return

    deletingIds.value = [...deletingIds.value, link.id]
    const toastId: ToastId = notification.loading('Excluindo link…')
    try {
        await betaStore.removeLinkBuildingLink(link.id)
        notification.update(toastId, 'Link excluído com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível excluir o link.',
            'error',
        )
    } finally {
        deletingIds.value = deletingIds.value.filter((id) => id !== link.id)
    }
}
</script>

<template>
    <div class="beta-link-building">
        <div class="beta-link-building__heading">
            <div>
                <p>Link Building</p>
                <h2>Sua página de links</h2>
                <a v-if="publicUrl" :href="publicUrl" target="_blank" rel="noopener noreferrer">
                    {{ publicUrl }} ↗
                </a>
                <span v-else>Personalize e organize os links compartilhados com seu público.</span>
            </div>
            <button type="button" @click="openCreate">+ Novo link</button>
        </div>

        <div v-if="betaStore.loadingLinkBuilding" class="beta-link-building__state">
            Carregando página de links…
        </div>

        <div v-else-if="betaStore.linkBuildingError" class="beta-link-building__state is-error">
            <span>{{ betaStore.linkBuildingError }}</span>
            <button type="button" @click="betaStore.loadLinkBuilding()">Tentar novamente</button>
        </div>

        <template v-else>
            <form class="beta-link-building__profile" @submit.prevent="savePage">
                <div class="beta-link-building__section-heading">
                    <div>
                        <h3>Aparência da página</h3>
                        <p>Escolha fundo, marca, título e descrição da página pública.</p>
                    </div>
                </div>

                <div class="beta-link-building__images">
                    <BetaPageImageField
                        label="Imagem de fundo"
                        description="Plano de fundo da página"
                        :image="pageDraft.background_file"
                        @select="chooseImage('background_file')"
                        @remove="pageDraft.background_file = ''"
                    />

                    <div class="beta-link-building__brand-image">
                        <label class="beta-link-building__field">
                            <span>Exibir no perfil</span>
                            <select v-model="pageDraft.logo_type">
                                <option value="1">Logo</option>
                                <option value="2">Avatar arredondado</option>
                            </select>
                        </label>

                        <BetaPageImageField
                            v-if="String(pageDraft.logo_type) === '1'"
                            label="Logo"
                            description="Marca exibida no topo"
                            :image="pageDraft.logo"
                            @select="chooseImage('logo')"
                            @remove="pageDraft.logo = ''"
                        />
                        <BetaPageImageField
                            v-else
                            label="Avatar"
                            description="Imagem arredondada do perfil"
                            :image="pageDraft.avatar"
                            @select="chooseImage('avatar')"
                            @remove="pageDraft.avatar = ''"
                        />
                    </div>
                </div>

                <div class="beta-link-building__fields">
                    <label class="beta-link-building__field">
                        <span>Título</span>
                        <input v-model="pageDraft.name" type="text" maxlength="120">
                    </label>
                    <label class="beta-link-building__field">
                        <span>Descrição</span>
                        <textarea v-model="pageDraft.description" rows="3" maxlength="300"></textarea>
                    </label>
                </div>

                <div class="beta-link-building__profile-actions">
                    <button type="submit" :disabled="!pageDirty || savingPage">
                        {{ savingPage ? 'Salvando…' : 'Salvar aparência' }}
                    </button>
                </div>
            </form>

            <section class="beta-link-building__links">
                <div class="beta-link-building__section-heading is-links">
                    <div>
                        <h3>Links</h3>
                        <p>Arraste os itens para definir a ordem exibida na página.</p>
                    </div>
                    <small>{{ betaStore.linkBuildingLinks.length }} links</small>
                </div>

                <div v-if="!betaStore.linkBuildingLinks.length" class="beta-link-building__empty">
                    <strong>Nenhum link adicionado</strong>
                    <span>Adicione uma URL ou um contato do WhatsApp.</span>
                    <button type="button" @click="openCreate">Adicionar link</button>
                </div>

                <draggable
                    v-else
                    :list="betaStore.linkBuildingLinks"
                    item-key="id"
                    handle=".beta-link-building-card__drag"
                    class="beta-link-building__list"
                    :disabled="sorting"
                    @end="sortLinks"
                >
                    <template #item="{ element }">
                        <BetaLinkBuildingCard
                            :link="element"
                            :deleting="deletingIds.includes(element.id)"
                            @edit="openEdit"
                            @remove="removeLink"
                        />
                    </template>
                </draggable>
            </section>
        </template>

        <BetaLinkBuildingDialog
            :open="dialogOpen"
            :link="linkForEdit"
            :default-ddi="applicationStore.appDefaultIdd ?? 30"
            :saving="savingLink"
            @close="closeDialog"
            @save="saveLink"
        />
    </div>
</template>

<style scoped>
.beta-link-building {
    display: grid;
    gap: 16px;
}

.beta-link-building__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf1f6;
}

.beta-link-building__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-link-building__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-link-building__heading a,
.beta-link-building__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-link-building__heading a {
    color: var(--main-color);
    font-weight: 750;
}

.beta-link-building__heading > button,
.beta-link-building__profile-actions button,
.beta-link-building__empty button,
.beta-link-building__state button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 14px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-link-building__profile,
.beta-link-building__links {
    border: 1px solid #e6ebf2;
    border-radius: 18px;
    background: #f9fbfd;
    padding: 15px;
}

.beta-link-building__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.beta-link-building__section-heading h3 {
    color: #263d5a;
    font-size: 14px;
    font-weight: 850;
}

.beta-link-building__section-heading p {
    margin-top: 3px;
    color: #8190a4;
    font-size: 12px;
}

.beta-link-building__section-heading > small {
    border-radius: 999px;
    background: #edf2f7;
    padding: 5px 8px;
    color: #77889e;
    font-size: 10px;
    font-weight: 800;
}

.beta-link-building__images {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
}

.beta-link-building__brand-image {
    display: grid;
    gap: 8px;
}

.beta-link-building__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 11px;
}

.beta-link-building__field {
    display: grid;
    gap: 5px;
}

.beta-link-building__field > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-link-building__field input,
.beta-link-building__field select,
.beta-link-building__field textarea {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 11px;
    background: #fff;
    padding: 9px 10px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-link-building__field input:focus,
.beta-link-building__field select:focus,
.beta-link-building__field textarea:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-link-building__profile-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 11px;
}

.beta-link-building__profile-actions button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.beta-link-building__list {
    display: grid;
    gap: 8px;
    margin-top: 11px;
}

.beta-link-building__empty,
.beta-link-building__state {
    display: grid;
    min-height: 160px;
    place-content: center;
    justify-items: center;
    gap: 6px;
    padding: 20px;
    color: #7e8da2;
    font-size: 12px;
    text-align: center;
}

.beta-link-building__empty strong {
    color: #304863;
    font-size: 14px;
}

.beta-link-building__empty button,
.beta-link-building__state button {
    margin-top: 5px;
}

.beta-link-building__state.is-error {
    color: #a34e4e;
}

@media (max-width: 700px) {
    .beta-link-building__heading {
        flex-direction: column;
    }

    .beta-link-building__images,
    .beta-link-building__fields {
        grid-template-columns: 1fr;
    }
}
</style>
